import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { CarouselItem, STATIC_CAROUSEL_ITEM } from '@/types/carousel.types'

export const selectCarouselItems = (state: RootState) => state.carousel.items

/**
 * Concatena los ítems dinámicos del store con {@link STATIC_CAROUSEL_ITEM} al final
 * y reasigna `order` 0-indexado. Memoizado con `createSelector` para evitar
 * re-renders cuando otros slices cambian sin afectar al carrusel.
 *
 * Este es el selector que `HeroCarousel` consume — los demás (incluyendo el modal)
 * usan `selectCarouselItems` para operar solo sobre los ítems dinámicos.
 */
export const selectCarouselItemsWithFixed = createSelector(
  [selectCarouselItems],
  (items): CarouselItem[] => [
    ...items.map((item, i) => ({ ...item, order: i })),
    { ...STATIC_CAROUSEL_ITEM, order: items.length },
  ]
)

export const selectCarouselIsLoading = (state: RootState) => state.carousel.isLoading
export const selectCarouselIsSaving = (state: RootState) => state.carousel.isSaving
export const selectCarouselError = (state: RootState) => state.carousel.error
export const selectCarouselLastUpdatedAt = (state: RootState) => state.carousel.lastUpdatedAt
export const selectIsEditModalOpen = (state: RootState) => state.carousel.isEditModalOpen

const selectPristineItems = (state: RootState) => state.carousel.pristineItems

/**
 * `true` si los ítems dinámicos difieren del snapshot tomado al abrir el modal
 * (`pristineItems`). Compara longitud, secuencia de `mediaId` y `fitMode`
 * (normalizado: ausente equivale a `'cover'`).
 */
export const selectIsCarouselDirty = createSelector(
  [selectCarouselItems, selectPristineItems],
  (items, pristine) => {
    if (items.length !== pristine.length) return true
    return items.some((it, i) => {
      if (it.mediaId !== pristine[i].mediaId) return true
      if ((it.fitMode ?? 'cover') !== (pristine[i].fitMode ?? 'cover')) return true
      return false
    })
  }
)
